import React, { useState, useEffect, useCallback, useRef } from "react";
import { ThemeContext } from "../../themes/ThemeContext";
import { getPartner, getOutflows, getInflows, exportPartnerInflows, exportPartnerOutflows } from "../../data/api/api";
import { getFailedTransactions } from "../../data/api/failedTransactions";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from "chart.js";
import { FaCreditCard } from "react-icons/fa";
import { BiTrendingDown, BiTrendingUp } from "react-icons/bi";
import NoData from "../../components/NoData/NoData";
import StatBox from "./components/StatBox";
import TableSection from "./components/TableSection";
import InflowVsOutFlowChart from "./components/InflowVsOutFlowChart";
import Loader from "../../components/Loader/Loader";
// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const PayOutDashboard = () => {
  const theme = React.useContext(ThemeContext);
  const partnerId = localStorage.getItem("pid");

  const [accountStats, setAccountStats] = useState({
    balance: 0.0,
    outflow: 0.0,
    inflow: 0.0,
  });

  const [inflowHistory, setInflowHistory] = useState([]);
  const [outflowHistory, setOutflowHistory] = useState([]);
  const [failedTransactions, setFailedTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const isMountedRef = useRef(true);
  const initialLoadDoneRef = useRef(false);

  const LOAD_TIMEOUT_MS = 30000;

  // Pagination state for inflows
  const [inflowPagination, setInflowPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });

  // Pagination state for outflows
  const [outflowPagination, setOutflowPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });

  // Pagination state for failed transactions
  const [failedTransactionsPagination, setFailedTransactionsPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });

  // Filter states for exports
  const [inflowFilters, setInflowFilters] = useState({
    date_from: '',
    date_to: '',
    amount_min: '',
    amount_max: '',
    sort_by: 'paid_at',
    sort_order: 'desc'
  });

  const [outflowFilters, setOutflowFilters] = useState({
    date_from: '',
    date_to: '',
    amount_min: '',
    amount_max: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });

  // Filter states for failed transactions
  const [failedTransactionsFilters, setFailedTransactionsFilters] = useState({
    date_from: '',
    date_to: '',
    amount_min: '',
    amount_max: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchInflows = useCallback(async () => {
    try {
      const inflowResponse = await getInflows(partnerId, inflowPagination.currentPage, inflowPagination.perPage);

      if (isMountedRef.current && inflowResponse.success) {
        const inflowData = inflowResponse.inflows || [];

        const processedInflows = inflowData.map((txn) => ({
          id: txn.id,
          transactionRef: txn.transaction_reference,
          payerAccountName: txn.payer_account_name,
          sessionId: txn.session_id,
          payerBank: txn.payer_bank,
          amount: parseFloat(txn.amount),
          date: new Date(txn.paid_at).toLocaleDateString(),
          details: txn.details,
        }));

        setInflowHistory(processedInflows);

        if (inflowResponse.pagination) {
          setInflowPagination((prev) => ({
            ...prev,
            total: inflowResponse.pagination.total || 0,
            currentPage: parseInt(inflowResponse.pagination.current_page, 10) || prev.currentPage,
            perPage: parseInt(inflowResponse.pagination.per_page, 10) || prev.perPage,
          }));
        }
      }
    } catch (error) {
      // console.error("Error fetching inflows:", error);
    }
  }, [inflowPagination.currentPage, inflowPagination.perPage, partnerId]);

  const fetchOutflows = useCallback(async () => {
    try {
      const outflowResponse = await getOutflows(partnerId, outflowPagination.currentPage, outflowPagination.perPage);

      if (isMountedRef.current && outflowResponse.success) {
        const outflowData = outflowResponse.outflows || [];

        const processedOutflows = outflowData.map((txn) => ({
          id: txn.id,
          transactionId: txn.transaction_id,
          transactionType: txn.transaction_type,
          transactionMethod: txn.transaction_method,
          charges: txn.charges,
          destinationAccount: txn.destination_acct,
          destinationAccountName: txn.destination_acct_name,
          destinationBank: txn.destination_bank,
          amount: parseFloat(txn.transaction_amount),
          date: new Date(txn.created_at).toLocaleDateString(),
        }));

        setOutflowHistory(processedOutflows);

        if (outflowResponse.pagination) {
          setOutflowPagination((prev) => ({
            ...prev,
            total: outflowResponse.pagination.total || 0,
            currentPage: parseInt(outflowResponse.pagination.current_page, 10) || prev.currentPage,
            perPage: parseInt(outflowResponse.pagination.per_page, 10) || prev.perPage,
          }));
        }
      }
    } catch (error) {
      // console.error("Error fetching outflows:", error);
    }
  }, [outflowPagination.currentPage, outflowPagination.perPage, partnerId]);

  const fetchFailedTransactions = useCallback(async () => {
    try {
      const failedTransactionsResponse = await getFailedTransactions(
        partnerId,
        failedTransactionsPagination.currentPage,
        failedTransactionsPagination.perPage,
        failedTransactionsFilters.date_from || null,
        failedTransactionsFilters.date_to || null
      );

      if (isMountedRef.current && failedTransactionsResponse.success) {
        const failedTransactionsData = failedTransactionsResponse.failedTransactions || [];

        const processedFailedTransactions = failedTransactionsData.map((txn) => ({
          id: txn.id,
          transactionReference: txn.transaction_reference,
          amount: parseFloat(txn.amount),
          charges: parseFloat(txn.charges),
          destinationAccount: txn.destination_account,
          destinationAccountName: txn.destination_account_name,
          destinationBank: txn.destination_bank,
          transactionType: txn.transaction_type,
          transactionMethod: txn.transaction_method,
          status: txn.status,
          narration: txn.narration,
          errorMessage: txn.response_message,
          errorCode: txn.error_code,
          responseMessage: txn.response_message,
          responseCode: txn.response_code,
          date: new Date(txn.created_at).toLocaleDateString(),
          createdAt: txn.created_at,
        }));

        setFailedTransactions(processedFailedTransactions);

        if (failedTransactionsResponse.pagination) {
          setFailedTransactionsPagination((prev) => ({
            ...prev,
            total: failedTransactionsResponse.pagination.total_count || 0,
            currentPage: parseInt(failedTransactionsResponse.pagination.current_page, 10) || prev.currentPage,
            perPage: parseInt(failedTransactionsResponse.pagination.per_page, 10) || prev.perPage,
          }));
        }
      }
    } catch (error) {
      // console.error("Error fetching failed transactions:", error);
    }
  }, [
    failedTransactionsFilters.date_from,
    failedTransactionsFilters.date_to,
    failedTransactionsPagination.currentPage,
    failedTransactionsPagination.perPage,
    partnerId
  ]);

  const fetchAllData = useCallback(async () => {
    try {
      const partnerResponse = await getPartner(partnerId);

      if (isMountedRef.current && partnerResponse.status === "success") {
        setAccountStats({
          balance: parseFloat(partnerResponse.data.account_balance) || 0,
          outflow: -parseFloat(partnerResponse.data.total_outflow_amount) || 0,
          inflow: parseFloat(partnerResponse.data.total_inflow) || 0,
        });
      }

      await Promise.all([
        fetchInflows(),
        fetchOutflows(),
        fetchFailedTransactions()
      ]);
    } catch (error) {
      // console.error("Error fetching all data:", error);
    }
  }, [fetchFailedTransactions, fetchInflows, fetchOutflows, partnerId]);

  const fetchInitialData = useCallback(async () => {
    if (!partnerId) {
      setIsLoading(false);
      setLoadError(null);
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timed out. The server may be slow or unreachable.")), LOAD_TIMEOUT_MS)
      );
      await Promise.race([fetchAllData(), timeoutPromise]);
    } catch (error) {
      setLoadError(error?.message || "Failed to load dashboard. Please try again.");
    } finally {
      setIsLoading(false);
      initialLoadDoneRef.current = true;
    }
  }, [fetchAllData, partnerId]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Handle pagination changes (skip on initial mount — fetchInitialData handles that)
  useEffect(() => {
    if (initialLoadDoneRef.current) {
      fetchInflows();
    }
  }, [fetchInflows, inflowPagination.currentPage, inflowPagination.perPage]);

  useEffect(() => {
    if (initialLoadDoneRef.current) {
      fetchOutflows();
    }
  }, [fetchOutflows, outflowPagination.currentPage, outflowPagination.perPage]);

  useEffect(() => {
    if (initialLoadDoneRef.current) {
      fetchFailedTransactions();
    }
  }, [failedTransactionsPagination.currentPage, failedTransactionsPagination.perPage, fetchFailedTransactions]);

  // Prepare data for the chart
  const prepareChartData = () => {
    const inflowData = {};
    const outflowData = {};

    // Group inflows by date
    inflowHistory.forEach((txn) => {
      const date = txn.date;
      if (!inflowData[date]) {
        inflowData[date] = 0;
      }
      inflowData[date] += txn.amount;
    });

    // Group outflows by date
    outflowHistory.forEach((txn) => {
      const date = txn.date;
      if (!outflowData[date]) {
        outflowData[date] = 0;
      }
      outflowData[date] += Math.abs(txn.amount); // Use absolute value for outflows
    });

    const dates = [...new Set([...Object.keys(inflowData), ...Object.keys(outflowData)])].sort();
    const inflowValues = dates.map((date) => inflowData[date] || 0);
    const outflowValues = dates.map((date) => outflowData[date] || 0);

    const chartData = {
      labels: dates,
      data: inflowValues,
      datasets: [
        {
          label: "Inflow",
          data: inflowValues,
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          fill: true,
        },
        {
          label: "Outflow",
          data: outflowValues,
          borderColor: "rgba(255, 99, 132, 1)",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          fill: true,
        },
      ],
    };

    return chartData;
  };

  const chartData = prepareChartData();

  // Handle page change for inflows
  const handleInflowPageChange = (page) => {
    // Ensure page is a valid number
    const requestedPage = parseInt(page, 10);
    if (isNaN(requestedPage)) {
      return;
    }
    
    // Ensure pagination values are numbers
    const total = parseInt(inflowPagination.total, 10) || 0;
    const perPage = parseInt(inflowPagination.perPage, 10) || 10;
    
    const totalPages = Math.ceil(total / perPage);
    const validPage = Math.max(1, Math.min(requestedPage, totalPages));
    
    // Update pagination state
    setInflowPagination((prev) => ({
      ...prev,
      currentPage: validPage,
      total: total,
      perPage: perPage
    }));
  };

  // Handle page change for outflows
  const handleOutflowPageChange = (page) => {
    // Ensure page is a valid number
    const requestedPage = parseInt(page, 10);
    if (isNaN(requestedPage)) {
      return;
    }
    
    // Ensure pagination values are numbers
    const total = parseInt(outflowPagination.total, 10) || 0;
    const perPage = parseInt(outflowPagination.perPage, 10) || 10;
    
    const totalPages = Math.ceil(total / perPage);
    const validPage = Math.max(1, Math.min(requestedPage, totalPages));
    
    // Update pagination state
    setOutflowPagination((prev) => ({
      ...prev,
      currentPage: validPage,
      total: total,
      perPage: perPage
    }));
  };

  const handleInflowExport = async () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(inflowFilters).filter(([_, value]) => value !== '')
    );
    
    if (Object.keys(cleanFilters).length === 0) {
      cleanFilters.sort_by = 'paid_at';
      cleanFilters.sort_order = 'desc';
    }
    
    return await exportPartnerInflows(partnerId, cleanFilters);
  };

  const handleOutflowExport = async () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(outflowFilters).filter(([_, value]) => value !== '')
    );
    
    if (Object.keys(cleanFilters).length === 0) {
      cleanFilters.sort_by = 'created_at';
      cleanFilters.sort_order = 'desc';
    }
    
    return await exportPartnerOutflows(partnerId, cleanFilters);
  };

  // Handle rows per page change for inflows
  const handleInflowRowsPerPageChange = (perPage) => {
    const newPerPage = parseInt(perPage, 10) || 10;
    setInflowPagination((prev) => ({ 
      ...prev, 
      perPage: newPerPage, 
      currentPage: 1 
    }));
  };

  // Handle rows per page change for outflows
  const handleOutflowRowsPerPageChange = (perPage) => {
    const newPerPage = parseInt(perPage, 10) || 10;
    setOutflowPagination((prev) => ({ 
      ...prev, 
      perPage: newPerPage, 
      currentPage: 1 
    }));
  };

  // Handle page change for failed transactions
  const handleFailedTransactionsPageChange = (page) => {
    const requestedPage = parseInt(page, 10);
    if (isNaN(requestedPage)) {
      return;
    }
    
    const total = parseInt(failedTransactionsPagination.total, 10) || 0;
    const perPage = parseInt(failedTransactionsPagination.perPage, 10) || 10;
    
    const totalPages = Math.ceil(total / perPage);
    const validPage = Math.max(1, Math.min(requestedPage, totalPages));
    
    setFailedTransactionsPagination((prev) => ({
      ...prev,
      currentPage: validPage,
      total: total,
      perPage: perPage
    }));
  };

  // Handle rows per page change for failed transactions
  const handleFailedTransactionsRowsPerPageChange = (perPage) => {
    const newPerPage = parseInt(perPage, 10) || 10;
    setFailedTransactionsPagination((prev) => ({ 
      ...prev, 
      perPage: newPerPage, 
      currentPage: 1 
    }));
  };

  // Handle failed transactions export
  const handleFailedTransactionsExport = async () => {
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(failedTransactionsFilters).filter(([_, value]) => value !== '')
      );
      
      if (Object.keys(cleanFilters).length === 0) {
        cleanFilters.sort_by = 'created_at';
        cleanFilters.sort_order = 'desc';
      }
      
      // For now, we'll use a simple CSV export since the API doesn't have an export endpoint
      const csvContent = generateFailedTransactionsCSV(failedTransactions);
      downloadCSV(csvContent, `failed-transactions-${Date.now()}.csv`);
      
      return { 
        success: true, 
        message: `Exported ${failedTransactions.length} failed transactions successfully` 
      };
    } catch (error) {
      // console.error('Failed transactions export error:', error);
      return { 
        success: false, 
        message: 'Failed to export failed transactions. Please try again.' 
      };
    }
  };

  // Generate CSV content for failed transactions
  const generateFailedTransactionsCSV = (transactions) => {
    const headers = [
      'Transaction Reference',
      'Amount',
      'Destination Account',
      'Destination Account Name',
      'Destination Bank',
      'Transaction Type',
      'Transaction Method',
      'Narration',
      'Response Message',
      'Error Code',
      'Date'
    ];
    
    const csvRows = [
      headers.join(','),
      ...transactions.map(txn => [
        txn.transactionReference || '',
        txn.amount || 0,
        txn.destinationAccount || '',
        `"${txn.destinationAccountName || ''}"`,
        `"${txn.destinationBank || ''}"`,
        txn.transactionType || '',
        txn.transactionMethod || '',
        `"${txn.narration || ''}"`,
        `"${txn.errorMessage || ''}"`,
        txn.errorCode || '',
        txn.date || ''
      ].join(','))
    ];
    
    return csvRows.join('\n');
  };

  // Download CSV file
  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <section className="mt-20 min-h-screen">
      {loadError ? (
        <div className="flex flex-col items-center justify-center py-20" style={{ color: theme?.colors?.text?.primary }}>
          <p className="text-red-500 mb-4 text-center max-w-md">{loadError}</p>
          <button
            onClick={fetchInitialData}
            className="px-6 py-2 rounded-lg font-medium transition-colors"
            style={{
              background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)",
              color: "#fff",
            }}
          >
            Retry
          </button>
        </div>
      ) : isLoading ? (
        <Loader message="Loading dashboard data..." />
      ) : (
        <>
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 mt-8">
            <StatBox 
              title="Account Balance" 
              value={`₦${accountStats.balance.toLocaleString()}`} 
              theme={theme} 
              icon={<FaCreditCard color="#151821" />} 
              type="balance"
            />
            
            <StatBox 
              title="Outflow" 
              value={`₦${Math.abs(accountStats.outflow).toLocaleString()}`} 
              theme={theme} 
              icon={<BiTrendingDown className="text-red-500" />} 
              isNegative 
              type="outflow"
            />
            
            <StatBox 
              title="Inflow" 
              value={`₦${accountStats.inflow.toLocaleString()}`} 
              theme={theme} 
              icon={<BiTrendingUp className="text-green-500" />} 
              isPositive 
              type="inflow"
            />
          </div>
          
          {/* Refresh Button */}
          {/* <div className="flex justify-end mt-4">
            <button
              onClick={fetchAllData}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {isLoading ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div> */}
          
          <InflowVsOutFlowChart 
            inflowHistory={inflowHistory} 
            outflowHistory={outflowHistory} 
            chartData={chartData} 
            theme={theme}
          />
          
          {/* Inflow History Table */}
          <div className="rounded-lg p-5 mt-8" style={{ color: "#d2d2d2", backgroundColor: theme.colors.background.secondary }}>
            <h3 className="text-lg font-bold mb-4">Inflow History</h3>
            {isLoading ? (
              <Loader message="Loading inflow data..." />
            ) : inflowHistory.length === 0 ? (
              <NoData message="No inflow data available" />
            ) : (
              <TableSection 
                data={inflowHistory} 
                columns={inflowColumns} 
                theme={theme} 
                pagination={inflowPagination}
                onPageChange={handleInflowPageChange}
                onRowsPerPageChange={handleInflowRowsPerPageChange}
                onExport={handleInflowExport}
                showFilters={true}
                showExport={true}
                filters={inflowFilters}
                onFiltersChange={setInflowFilters}
                defaultFilters={{
                  sort_by: 'paid_at',
                  sort_order: 'desc'
                }}
                sortFieldOptions={[
                  { value: 'paid_at', label: 'Paid At' },
                  { value: 'amount', label: 'Amount' },
                  { value: 'created_at', label: 'Created At' }
                ]}
              />
            )}
          </div>
          
          {/* Outflow History Table */}
          <div className="rounded-lg p-5 mt-8" style={{ color: "#d2d2d2", backgroundColor: theme.colors.background.secondary }}>
            <h3 className="text-lg font-bold mb-4">Outflow History</h3>
            {isLoading ? (
              <Loader message="Loading outflow data..." />
            ) : outflowHistory.length === 0 ? (
              <NoData message="No outflow data available" />
            ) : (
              <TableSection 
                data={outflowHistory} 
                columns={outflowColumns} 
                theme={theme} 
                pagination={outflowPagination}
                onPageChange={handleOutflowPageChange}
                onRowsPerPageChange={handleOutflowRowsPerPageChange}
                onExport={handleOutflowExport}
                showFilters={true}
                showExport={true}
                filters={outflowFilters}
                onFiltersChange={setOutflowFilters}
                defaultFilters={{
                  sort_by: 'created_at',
                  sort_order: 'desc'
                }}
                sortFieldOptions={[
                  { value: 'created_at', label: 'Created At' },
                  { value: 'transaction_amount', label: 'Transaction Amount' },
                  { value: 'updated_at', label: 'Updated At' }
                ]}
              />
            )}
          </div>

          {/* Failed Transactions Table */}
          <div className="rounded-lg p-5 mt-8" style={{ color: "#d2d2d2", backgroundColor: theme.colors.background.secondary }}>
            <h3 className="text-lg font-bold mb-4">Failed Transactions</h3>
            {isLoading ? (
              <Loader message="Loading failed transactions..." />
            ) : failedTransactions.length === 0 ? (
              <NoData message="No failed transactions found" />
            ) : (
              <TableSection 
                data={failedTransactions} 
                columns={failedTransactionsColumns} 
                theme={theme} 
                pagination={failedTransactionsPagination}
                onPageChange={handleFailedTransactionsPageChange}
                onRowsPerPageChange={handleFailedTransactionsRowsPerPageChange}
                onExport={handleFailedTransactionsExport}
                showFilters={true}
                showExport={true}
                filters={failedTransactionsFilters}
                onFiltersChange={setFailedTransactionsFilters}
                defaultFilters={{
                  sort_by: 'created_at',
                  sort_order: 'desc'
                }}
                sortFieldOptions={[
                  { value: 'created_at', label: 'Created At' },
                  { value: 'amount', label: 'Amount' },
                  { value: 'transaction_reference', label: 'Transaction Reference' }
                ]}
              />
            )}
          </div>
        </>
      )}
    </section>
  );
};

const inflowColumns = [
  { key: "transactionRef", label: "Transaction Reference" },
  { key: "payerAccountName", label: "Payer Account Name" },
  { key: "sessionId", label: "Session ID" },
  { key: "payerBank", label: "Payer Bank" },
  { key: "amount", label: "Amount" },
  { key: "date", label: "Date" },
  { key: "details", label: "Details" },
];

const outflowColumns = [
  { 
    key: "transactionId", 
    label: "Transaction ID",
    format: (value) => {
      if (value && value.includes('|')) {
        return value.split('|')[0];
      }
      return value;
    }
  },
  { key: "transactionType", label: "Transaction Type" },
  { key: "transactionMethod", label: "Transaction Method" },
  { key: "charges", label: "Charges" },
  { key: "destinationAccount", label: "Destination Acct" },
  { key: "destinationAccountName", label: "Dest Acct Name" },
  { key: "destinationBank", label: "Destination Bank" },
  { key: "amount", label: "Amount" },
  { key: "date", label: "Date" },
];

const failedTransactionsColumns = [
  { 
    key: "transactionReference", 
    label: "Transaction Reference",
    format: (value) => {
      if (value && value.includes('|')) {
        return value.split('|')[0];
      }
      return value;
    }
  },
  { 
    key: "amount", 
    label: "Amount",
    format: (value) => `₦${value?.toLocaleString() || '0'}`
  },
  { key: "destinationAccount", label: "Destination Account" },
  { key: "destinationAccountName", label: "Account Name" },
  { key: "destinationBank", label: "Bank" },
  { key: "transactionType", label: "Type" },
  { key: "transactionMethod", label: "Method" },
  { 
    key: "errorMessage", 
    label: "Response Message",
    format: (value) => value || 'N/A'
  },
  { key: "date", label: "Date" },
];

export default PayOutDashboard;

